import type { ComponentConfig } from "@puckeditor/core";
import { formatPhoneDisplay } from "../../lib/phone-formatter";
import { resolvesToPhoneUrl, SECONDARY_FORM_CTA_TEXT, contactUrl } from "../../lib/cta-defaults";
import { resolveBusinessCtaText } from "../../lib/business-bound-prop";
import { getWindowJobsiteConfig, type JobsiteBusinessContext } from "../../types/runtime-config";
import { CTA, type CTAProps } from "./cta";
import { CTAFields } from "./cta.fields";
import { RegenerableTextField } from "../editor/RegenerableTextField";
import { IconPicker } from "../editor/IconPicker";
import { ImagePicker } from "../editor/ImagePicker";
import { withBusinessCtaOverride } from "../../lib/business-bound-fields";

export const CTAConfig: ComponentConfig<CTAProps> = {
  label: "Call to Action",
  fields: {
    headline: {
      ...CTAFields.headline,
      contentEditable: true,
      render: ({ value, onChange, field }) => (
        <RegenerableTextField
          value={value || ""}
          onChange={onChange}
          namespace="home.cta.headline"
          label="Headline"
          multiline={true}
          fieldMeta={field?.metadata}
        />
      ),
    },
    subheadline: {
      ...CTAFields.subheadline,
      contentEditable: true,
      render: ({ value, onChange, field }) => (
        <RegenerableTextField
          value={value || ""}
          onChange={onChange}
          namespace="home.cta.subheadline"
          label="Subheadline (optional)"
          multiline={true}
          fieldMeta={field?.metadata}
        />
      ),
    },
    ctaText: {
      ...CTAFields.ctaText,
      contentEditable: true,
    },
    ctaUrl: CTAFields.ctaUrl,
    backgroundImage: {
      ...CTAFields.backgroundImage,
      render: ({
        value,
        onChange,
      }: {
        value: string | undefined;
        onChange: (val: string | undefined) => void;
      }) => <ImagePicker value={value || ""} onChange={onChange} label="Background Image" />,
    },
    imagePosition: CTAFields.imagePosition,
    overlay: CTAFields.overlay,
    variant: CTAFields.variant,
    showTrustSignals: CTAFields.showTrustSignals,
    trustSignals: {
      ...CTAFields.trustSignals,
      arrayFields: {
        text: CTAFields.trustSignals.arrayFields.text,
        icon: {
          ...CTAFields.trustSignals.arrayFields.icon,
          render: ({
            value,
            onChange,
          }: {
            value: string | undefined;
            onChange: (val: string | undefined) => void;
          }) => <IconPicker value={value || ""} onChange={onChange} label="Icon" />,
        },
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any,
  },
  /**
   * The Settings label wins over the saved page value by design; this makes the editor say
   * so instead of offering a box that silently discards what you type.
   */
  resolveFields: (_data, { fields, metadata }) =>
    withBusinessCtaOverride(fields as Record<string, unknown>, "ctaText", metadata) as typeof fields,
  defaultProps: {
    headline: "Ready to Experience the Difference?",
    subheadline:
      "Join thousands of satisfied customers who trust us for quality service and reliable results.",
    ctaText: "Get Started Today",
    ctaUrl: "/contact",
    secondaryCtaText: "Call Now",
    secondaryCtaUrl: "tel:",
    backgroundImage: "", // No default - will use solid background color
    imagePosition: "center",
    overlay: "dark",
    variant: "band",
    showTrustSignals: "yes",
    trustSignals: [
      { text: "Free Consultation", icon: "CheckCircle" },
      { text: "Top Rated Service", icon: "Star" },
      { text: "Quick Response Time", icon: "Calendar" },
      { text: "Licensed & Insured", icon: "Shield" },
    ],
  },
  render: ({ id, puck, ...props }) => {
    // Access business data from Puck metadata OR window config (fallback)
    const puckMetadata = puck?.metadata as { business?: JobsiteBusinessContext } | undefined;
    const windowConfig = getWindowJobsiteConfig();
    const business = puckMetadata?.business || windowConfig?.business;

    // Get phone number - check both 'phone' and 'telephone' property names
    const businessPhone = business?.phone || business?.telephone;

    // Resolve the effective primary CTA via the shared precedence helper.
    const finalCtaText = resolveBusinessCtaText(props.ctaText, business);
    const wantsPhoneUrl = resolvesToPhoneUrl(finalCtaText);

    // The two buttons must offer two different actions — same rule as the hero.
    // The secondary used to be the phone unconditionally, so a phone-first CTA
    // shipped "Call Now" beside "Call (877) 806-3490", both the same tel: link.
    const secondaryCta = (): { text: string; url: string } | undefined => {
      if (wantsPhoneUrl) {
        return { text: SECONDARY_FORM_CTA_TEXT, url: "/contact" };
      }
      if (businessPhone) {
        return { text: `Call ${formatPhoneDisplay(businessPhone)}`, url: `tel:${businessPhone}` };
      }
      return undefined;
    };
    const secondary = secondaryCta();

    const resolvedProps = {
      ...props,
      ctaText: finalCtaText,
      // The saved ctaUrl is a generator default ("/contact"), not a choice
      // anyone made — a "Call …" primary must actually call (hero rule).
      ctaUrl:
        wantsPhoneUrl && businessPhone ? `tel:${businessPhone}` : contactUrl(props.ctaUrl),
      secondaryCtaText: secondary?.text,
      secondaryCtaUrl: secondary?.url,
      variant: props.variant || "band",
    };

    return <CTA id={id} {...resolvedProps} />;
  },
};
